import React from 'react';
import { ArrowRight, Play, CheckCircle, Users, Award, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import IrisAvatar from './IrisAvatar';

interface HeroProps {
  onAdminClick?: () => void;
  onProjectSubmit?: () => void;
  onAppointmentBook?: () => void;
  isAuthenticated?: boolean;
  onLoginClick?: () => void;
}

const Hero: React.FC<HeroProps> = ({ onAdminClick, onProjectSubmit, onAppointmentBook, isAuthenticated, onLoginClick }) => {
  const navigate = useNavigate();
  
  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleOpenChat = () => {
    window.alert('Bientôt disponible');
  };

  const stats = [
    { number: '250+', label: 'Projets réalisés', icon: Award },
    { number: '98%', label: 'Clients satisfaits', icon: Users },
    { number: '5 ans', label: 'D\'expérience', icon: Clock },
    { number: '24/7', label: 'Support client', icon: CheckCircle },
  ];

  const features = [
    'Développement sur mesure',
    'Technologies modernes',
    'Équipe experte',
    'Support continu',
    'Livraison rapide',
    'Prix compétitifs'
  ];

  return (
    <>
      <section id="home" className="relative bg-ice-light overflow-hidden">
      {/* Grande bannière */}
      <div className="relative h-screen">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url(/baniere.jpg)' }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent"></div>
        </div>
        
        {/* Texte Iris Softech supprimé - maintenant dans HomePage */}

        {/* Navigation supprimée - maintenant dans HomePage */}

        {/* Navigation Mobile supprimée - maintenant dans HomePage */}
        
        {/* Contenu superposé */}
        <div className="relative z-10 h-full flex items-center">
          <div className="container-max">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Contenu texte */}
              <div className="text-white">
                {/* Contenu principal */}
                
                <h2 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                  Solutions digitales
                  <span className="block text-amber-400">sur mesure</span>
                </h2>
                
                <p className="text-xl text-gray-200 mb-8 leading-relaxed max-w-2xl">
                  Nous concevons et développons des applications web et mobiles performantes 
                  qui propulsent votre business vers le succès. De l'idée à la mise en production.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <button
                    onClick={() => navigate('/submitprojects')}
                    className="btn-primary flex items-center justify-center space-x-2"
                  >
                    <span>Soumettre un projet</span>
                    <ArrowRight className="w-5 h-5" />
                  </button>
                  
                  <button
                    onClick={() => navigate('/bookingappointment')}
                    className="btn-primary flex items-center justify-center space-x-2"
                  >
                    <Play className="w-5 h-5" />
                    <span>Prendre rendez-vous</span>
                  </button>
                </div>

                {/* Features */}
                <div className="grid grid-cols-2 gap-3">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-center text-sm text-gray-200">
                      <CheckCircle className="w-4 h-4 text-green-400 mr-2 flex-shrink-0" />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>

              {/* Avatar Iris IA */}
              <div className="lg:ml-12">
                <IrisAvatar 
                  showMessage={true}
                  size="large"
                  onClick={handleOpenChat}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Indicateur de scroll - Desktop seulement */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 hidden md:block">
          <button
            onClick={() => scrollToSection('#services')}
            className="w-8 h-12 border-2 border-white/50 rounded-full flex justify-center items-start pt-2 hover:border-white/80 transition-colors"
          >
            <div className="w-1 h-3 bg-white/70 rounded-full animate-bounce"></div>
          </button>
        </div>
      </div>

      {/* Section informations complémentaires */}
      <div className="bg-ice py-16">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Pourquoi choisir Iris Softech ?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Notre expertise technique combinée à notre approche centrée client 
              garantit le succès de vos projets digitaux.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card hover-lift text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Expertise reconnue
              </h3>
              <p className="text-gray-600">
                Plus de 5 ans d'expérience dans le développement d'applications 
                web et mobiles de haute qualité.
              </p>
            </div>

            <div className="card hover-lift text-center">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Équipe dédiée
              </h3>
              <p className="text-gray-600">
                Une équipe passionnée de développeurs, designers et chefs de projet 
                à votre service.
              </p>
            </div>

            <div className="card hover-lift text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Satisfaction garantie
              </h3>
              <p className="text-gray-600">
                98% de nos clients nous recommandent. Notre priorité : 
                votre succès et votre satisfaction.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default Hero;
